import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    AppBar,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    Badge,
} from '@mui/material';
import {
    ToolbarPaper,
    ToolbarContainer,
    LogoBox,
    LogoImage,
    DarkBlueButton,
    Overlay,
} from './TopNavBar.styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../../api/authApi';
import { getProfile } from '../../api/api';
import { getUserType, getTeamMemberDefaultRoute } from '../../utils/navigation';
import {
    getCartUnified,
    mergeGuestCartIntoServerIfAny,
} from '../../cart/cartBridge';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CartContainer from '../CartContainer/CartContainer';

const TopNavbar = ({ onTabChange }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const isTokenValid = (token) => token && token !== 'undefined' && token !== 'null';

    const [isLoggedIn, setIsLoggedIn] = useState(() => {
        const token = localStorage.getItem('accessToken');
        return isTokenValid(token);
    });

    const [userType, setUserType] = useState(() => getUserType());

    const [username, setUsername] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [cartCount, setCartCount] = useState(0);

    const open = Boolean(anchorEl);
    const showCartIcon = location.pathname === '/landing/products';
    const showShopNowButton = location.pathname !== '/landing/products';

    const computeCartCount = useCallback((cart) => {
        if (!cart) return 0;
        if (typeof cart.item_count === 'number') return cart.item_count;
        const items = Array.isArray(cart.items) ? cart.items : [];
        return items.reduce((acc, item) => acc + Number(item.quantity || 0), 0);
    }, []);

    const refreshCartCount = useCallback(async () => {
        try {
            const cart = await getCartUnified();
            setCartCount(computeCartCount(cart));
        } catch (err) {
            console.error('Cart refresh failed:', err);
            setCartCount(0);
        }
    }, [computeCartCount]);

    useEffect(() => {
        (async () => {
            try {
                await mergeGuestCartIntoServerIfAny();
            } finally {
                await refreshCartCount();
            }
        })();
    }, [refreshCartCount]);

    useEffect(() => {
        const handler = () => {
            setTimeout(refreshCartCount, 100);
        };

        window.addEventListener('cart:updated', handler);
        window.addEventListener('storage', handler);

        return () => {
            window.removeEventListener('cart:updated', handler);
            window.removeEventListener('storage', handler);
        };
    }, [refreshCartCount]);

    const handleCartButtonClick = () => setIsCartOpen(true);
    const handleOverlayClick = () => {
        setIsCartOpen(false);
        setTimeout(refreshCartCount, 150);
    };

    // Check if cart should be opened from navigation state (e.g., after login/register)
    useEffect(() => {
        if (location.state?.openCart === true) {
            setIsCartOpen(true);
            // Clear the state to prevent reopening on subsequent navigations
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    useEffect(() => {
        const checkLogin = async () => {
            const token = localStorage.getItem('accessToken');
            const loggedIn = isTokenValid(token);
            setIsLoggedIn(loggedIn);
            setUserType(getUserType());

            if (loggedIn) {
                try {
                    const profile = await getProfile();
                    if (profile?.full_name) {
                        setUsername(profile.full_name);
                        localStorage.setItem('username', profile.full_name);
                    }
                } catch (err) {
                    console.error('Failed to load user profile:', err);
                }
            } else {
                setUsername('');
            }
        };

        checkLogin();

        window.addEventListener('login', checkLogin);
        window.addEventListener('logout', checkLogin);

        return () => {
            window.removeEventListener('login', checkLogin);
            window.removeEventListener('logout', checkLogin);
        };
    }, []);

    const handleLoginClick = () => {
        onTabChange?.(0);
        navigate('/login');
    };

    const handleRegisterClick = () => {
        onTabChange?.(1);
        navigate('/register');
    };

    const handleLogoutClick = async () => {
        const token = localStorage.getItem('accessToken');
        try {
            await logout(token);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('isAdmin');
            localStorage.removeItem('username');
            setIsLoggedIn(false);
            setUsername('');
            window.dispatchEvent(new Event('logout'));
            navigate('/landing');
        } catch (err) {
            console.error('Logout failed:', err);
        }
    };

    const handleShopNowClick = () => navigate('/landing/products');
    const handleSettingsClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleMenuItemClick = (path) => {
        navigate(path);
        handleMenuClose();
    };

    return (
        <AppBar position="static" color="transparent" elevation={0}>
            <ToolbarPaper elevation={0}>
                <ToolbarContainer>
                    <LogoBox onClick={() => navigate('/landing')} sx={{ cursor: 'pointer' }}>
                        <LogoImage src="/APD_TopNav.png" alt="Logo" />
                    </LogoBox>

                    <Box display="flex" alignItems="center" gap={1}>
                        {isLoggedIn && username && (
                            <Box display="flex" alignItems="center" sx={{ mr: 0.5, color: '#000000' }}>
                                {userType !== 'customer' ? (
                                    <SupervisedUserCircleIcon sx={{ color: '#000000', mr: 0.8 }} />
                                ) : (
                                    <AccountCircleIcon sx={{ color: '#000000', mr: 0.8 }} />
                                )}
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                                >
                                    {username}
                                </Typography>
                            </Box>
                        )}

                        {showCartIcon && (
                            <IconButton
                                aria-label="open cart"
                                onClick={handleCartButtonClick}
                                sx={{ mr: 1 }}
                            >
                                {cartCount > 0 ? (
                                    <Badge color="primary" badgeContent={cartCount}>
                                        <ShoppingCartIcon sx={{ color: '#002F87' }} />
                                    </Badge>
                                ) : (
                                    <ShoppingCartIcon sx={{ color: '#002F87' }} />
                                )}
                            </IconButton>
                        )}

                        {showShopNowButton && (
                            <DarkBlueButton
                                onClick={handleShopNowClick}
                                sx={{
                                    backgroundColor: '#ffffff',
                                    color: '#002F87',
                                    border: '1px solid #002F87',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                        borderColor: '#002F87',
                                    },
                                }}
                            >
                                Shop Now
                            </DarkBlueButton>
                        )}

                        {isLoggedIn && (
                            <IconButton
                                onClick={handleSettingsClick}
                                sx={{
                                    color: '#002F87',
                                    p: 1,
                                    minWidth: 48,
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)', borderRadius: 1 },
                                }}
                                aria-label="settings"
                            >
                                <SettingsIcon fontSize="medium" />
                            </IconButton>
                        )}

                        {!isLoggedIn && (
                            <>
                                <DarkBlueButton onClick={handleLoginClick}>Login</DarkBlueButton>
                                <DarkBlueButton color="primary" onClick={handleRegisterClick}>
                                    Register
                                </DarkBlueButton>
                            </>
                        )}

                        {isCartOpen && (
                            <Overlay onClick={handleOverlayClick}>
                                <div onClick={(e) => e.stopPropagation()}>
                                    <CartContainer onClose={() => setIsCartOpen(false)} />
                                </div>
                            </Overlay>
                        )}
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem onClick={() => handleMenuItemClick('/landing')}>Home</MenuItem>
                        {userType === 'admin' ? (
                            <MenuItem onClick={() => handleMenuItemClick('/product-management')}>
                                Admin Portal
                            </MenuItem>
                        ) : userType === 'team_member' ? (
                            <MenuItem onClick={() => handleMenuItemClick(getTeamMemberDefaultRoute() || '/order-management')}>
                                Dashboard
                            </MenuItem>
                        ) : (
                            <MenuItem onClick={() => handleMenuItemClick('/user-portal')}>
                                Customer Portal
                            </MenuItem>
                        )}
                        <MenuItem onClick={handleLogoutClick}>Logout</MenuItem>
                    </Menu>
                </ToolbarContainer>
            </ToolbarPaper>
        </AppBar>
    );
};

export default TopNavbar;
